import { useEffect, useRef } from 'react';

/**
 * Refresca el access token un poco antes de su `exp` (claim del JWT) para
 * evitar que los requests del usuario reciban 401 y disparen el flujo
 * reactivo de refresh.
 *
 * - Lee el `exp` del access token actual.
 * - Programa un setTimeout para llamar a authService.refreshToken().
 * - Se reprograma tras cada refresh exitoso.
 * - Si la pestaña vuelve de background, recalcula por si quedó "atrás" del reloj.
 */

const REFRESH_LEAD_TIME_MS = 2 * 60 * 1000; // refrescar 2 min antes de expirar
const MIN_DELAY_MS = 5 * 1000;              // nunca menos de 5s

const decodeJwtExp = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const useProactiveTokenRefresh = (enabled: boolean = true) => {
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const clearScheduled = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const schedule = () => {
      clearScheduled();

      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const expMs = decodeJwtExp(token);
      if (!expMs) return;

      const now = Date.now();
      const delay = Math.max(MIN_DELAY_MS, expMs - now - REFRESH_LEAD_TIME_MS);

      timeoutRef.current = window.setTimeout(async () => {
        if (cancelled) return;
        try {
          const { authService } = await import('../services/authService');
          await authService.refreshToken();
        } catch {
          // El interceptor reactivo se encargará si esto falla
        } finally {
          if (!cancelled) schedule();
        }
      }, delay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        schedule();
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        schedule();
      }
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth:token-refreshed', schedule);

    return () => {
      cancelled = true;
      clearScheduled();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:token-refreshed', schedule);
    };
  }, [enabled]);
};
